import Group from 'components/utils/Group'
import ModalBody from 'LuxKit/Modal/ModalBody'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import SocialButtons from 'components/Account/AccountAccess/AccountAccessSocialButtons'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import React, { FormEventHandler, useCallback, useContext, useEffect, useRef, useState } from 'react'
import TextInput from 'components/Common/Form/Input/TextInput'
import ModalContent from 'LuxKit/Modal/ModalContent'
import TextButton from 'LuxKit/Button/TextButton'
import { useMutation } from '@tanstack/react-query'
import { checkUserExistsForBrand } from 'api/auth'
import ReCAPTCHA from 'react-google-recaptcha'
import config from 'constants/config'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import { formToObject } from 'lib/forms/formToObject'
import handleReCAPTCHA from 'businessTraveller/utils/handleReCAPTCHA'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import { LUXURY_ESCAPES } from 'constants/brands'
import { BUSINESS_TRAVELLER_PERSONAL_ACCOUNT_LINKING_ACTIONS, BUSINESS_TRAVELLER_PERSONAL_ACCOUNT_LINKING_STEPS, BusinessTravellerPersonalAccountLinkingDispatchContext } from 'businessTraveller/contexts/BusinessTravellerPersonalAccountLinkingContexts'
import { BUSINESS_TRAVELLER_ACTIONS } from 'reducers/businessTravellerActionReducer'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'

interface FormData {
  personalEmail: string
}

function BusinessTravellerPersonalEmailUpdateSetupStep() {
  const recaptchaRef = useRef<ReCAPTCHA>(null)
  const [googleSSOError, _setGoogleSSOError] = useState<string | null>(null)
  const accountLinkingDispatch = useContext(BusinessTravellerPersonalAccountLinkingDispatchContext)
  const dispatch = useAppDispatch()
  const hasLinkedAccount = useAppSelector(state => !!state.businessTraveller.currentBusinessAccount?.employee?.creditCustomerId)
  const isDedicatedTenantRegistrationEnabled = !!useOptimizelyExperiment(OptimizelyFeatureFlags.lebtDedicatedTenantRegistration)

  const emailCheckMutation = useMutation({
    mutationFn: async(params: { email: string }) => {
      // Reset so the invisible reCAPTCHA issues a fresh single-use token on retry — otherwise
      // executeAsync can hang on the token a previous failed lookup already consumed.
      recaptchaRef.current?.reset()
      const recaptchaResponse = await handleReCAPTCHA(recaptchaRef.current)
      // Credit account lives in the LE brand when flag-on, so check that brand to decide link-vs-create; default (LEBT) otherwise.
      const brand = isDedicatedTenantRegistrationEnabled ? LUXURY_ESCAPES : undefined
      return checkUserExistsForBrand({ email: params.email }, recaptchaResponse, brand)
    },
    onSuccess: (data, variables) => {
      accountLinkingDispatch({
        type: BUSINESS_TRAVELLER_PERSONAL_ACCOUNT_LINKING_ACTIONS.SET_PERSONAL_EMAIL,
        personalEmail: variables.email,
      })
      if (data?.isExist) {
        accountLinkingDispatch({
          type: BUSINESS_TRAVELLER_PERSONAL_ACCOUNT_LINKING_ACTIONS.STEP_INTO,
          step: BUSINESS_TRAVELLER_PERSONAL_ACCOUNT_LINKING_STEPS.EXISTING_PERSONAL_USER,
        })
      } else {
        accountLinkingDispatch({
          type: BUSINESS_TRAVELLER_PERSONAL_ACCOUNT_LINKING_ACTIONS.STEP_INTO,
          step: BUSINESS_TRAVELLER_PERSONAL_ACCOUNT_LINKING_STEPS.NEW_PERSONAL_USER,
        })
      }
    },
  })

  // Handle social login success
  const socialLoginSuccessMutation = useMutation({
    mutationFn: async(email: string) => {
      return Promise.resolve({ email })
    },
    onSuccess: (data) => {
      dispatch({
        type: BUSINESS_TRAVELLER_ACTIONS.UPDATE_CREDIT_CUSTOMER_DETAILS,
        creditCustomerEmail: data.email,
      })
      accountLinkingDispatch({
        type: BUSINESS_TRAVELLER_PERSONAL_ACCOUNT_LINKING_ACTIONS.SET_PERSONAL_EMAIL,
        personalEmail: data.email,
      })
      accountLinkingDispatch({
        type: BUSINESS_TRAVELLER_PERSONAL_ACCOUNT_LINKING_ACTIONS.STEP_INTO,
        step: BUSINESS_TRAVELLER_PERSONAL_ACCOUNT_LINKING_STEPS.PERSONAL_ACCOUNT_LINK_CONFIRMATION,
      })
    },
  })

  // Listen for Google login success and trigger step navigation
  useEffect(() => {
    const handleGoogleLoginSuccess = (event: CustomEvent) => {
      const email = event.detail?.email || ''
      socialLoginSuccessMutation.mutate(email)
    }

    window.addEventListener('google-login-success', handleGoogleLoginSuccess as EventListener)

    return () => {
      window.removeEventListener('google-login-success', handleGoogleLoginSuccess as EventListener)
    }
  }, [socialLoginSuccessMutation])

  const handleSubmission = useCallback<FormEventHandler<HTMLFormElement>>((event) => {
    event.preventDefault()

    const formData = formToObject<FormData>(event.currentTarget)

    emailCheckMutation.mutate({ email: formData.personalEmail })
  }, [emailCheckMutation])

  const isBusy = emailCheckMutation.isLoading

  return <form onSubmit={handleSubmission}>
    <ModalHeader title="" />
    <ModalBody>
      <ModalContent>
        <Group direction="vertical" gap={24}>
          <Group direction="vertical" gap={12}>
            <Heading variant="heading4">
              {hasLinkedAccount ? 'Change' : 'Set'} linked Luxury Escapes account
            </Heading>

            <BodyText variant="large">
              Credit you earned will be allocated to this Luxury Escapes account for booking personal travel.
            </BodyText>
          </Group>

          <Group direction="vertical" gap={12}>
            <TextInput
              name="personalEmail"
              type="email"
              autoComplete="email"
              label="Enter your email"
              placeholder="Email address"
              helpText="We'll check if you have a profile with Luxury Escapes or if we need to create one for you."
              required
              autoFocus
            />

            <TextButton
              type="submit"
              kind="primary"
              size="large"
              disabled={isBusy}
            >
              Continue
            </TextButton>

            {!!config.GOOGLE_APP_ID && <>
              <SocialButtons
                mode="login"
                callbackSource="business-traveller-personal-account-linking"
              />

              {!!googleSSOError && <MessageBanner
                description={googleSSOError}
                kind="critical"
              />}
            </>}

            <BodyText variant="small" colour="neutral-three">
              By continuing you agree to our Terms &amp; Conditions and Privacy Policy and to subscribe to emails for offers, alerts and services.
            </BodyText>
          </Group>
        </Group>
        {config.RECAPTCHA_KEY && <ReCAPTCHA size="invisible" sitekey={config.RECAPTCHA_KEY} ref={recaptchaRef} />}
      </ModalContent>
    </ModalBody>
  </form>
}

export default BusinessTravellerPersonalEmailUpdateSetupStep
